import { createSlice } from '@reduxjs/toolkit';
import { UiState } from 'constants/ui';
import { ReturnShippingOption, ReturnShippingMethodPayingSide } from 'constants/return-shipping-option';
import { stateName } from './constants';
export const initialState = {
  returnShipment: undefined,
  shipmentItems: undefined,
  returnShipmentOptions: undefined,
  integratedShipmentOption: undefined,
  customShipmentOption: undefined,
  returnShipmentOrder: undefined,
  ui: {
    error: null,
    uiState: UiState.Idle,
    selectedShipmentOption: undefined,
    selectedPaymentOptionSide: undefined,
    activeModal: undefined,
    returnLabelOrder: {
      fetchUiState: UiState.Idle,
      prepareUiState: UiState.Idle
    }
  }
};

const getReturnShipmentRequest = draft => {
  draft.ui.uiState = UiState.Pending;
};

const getReturnShipmentFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.uiState = UiState.Failure;
  draft.ui.error = error;
};

const getReturnShipmentSuccess = (draft, action) => {
  var _action$payload$payer;

  draft.ui.uiState = UiState.Success;
  draft.ui.error = null;
  draft.returnShipment = action.payload;

  if ((_action$payload$payer = action.payload.payerSelection) !== null && _action$payload$payer !== void 0 && _action$payload$payer.length) {
    if (draft.ui.selectedPaymentOptionSide === undefined || draft.ui.selectedPaymentOptionSide === ReturnShippingMethodPayingSide.Seller) {
      draft.ui.selectedPaymentOptionSide = ReturnShippingMethodPayingSide.Seller;
    } else {
      draft.ui.selectedPaymentOptionSide = ReturnShippingMethodPayingSide.Buyer;
    }
  }
};

const getShipmentItemsRequest = draft => {
  draft.ui.uiState = UiState.Pending;
};

const getShipmentItemsFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.uiState = UiState.Failure;
  draft.ui.error = error;
};

const getShipmentItemsSuccess = (draft, action) => {
  draft.ui.uiState = UiState.Success;
  draft.ui.error = null;
  draft.shipmentItems = action.payload;
};

const getReturnShippingOptionsRequest = draft => {
  draft.ui.uiState = UiState.Pending;
};

const getReturnShippingOptionsFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.uiState = UiState.Failure;
  draft.ui.error = error;
};

const getReturnShippingOptionsSuccess = (draft, action) => {
  const {
    returnShipment,
    currencyConversion
  } = action.payload;
  const integratedShipmentOption = returnShipment.find(shipmentOption => shipmentOption.code === ReturnShippingOption.Integrated);
  const customShipmentOption = returnShipment.find(shipmentOption => shipmentOption.code === ReturnShippingOption.Custom);

  if (currencyConversion) {
    draft.currencyConversion = currencyConversion;
  }

  draft.ui.uiState = UiState.Success;
  draft.ui.error = null;
  draft.returnShipmentOptions = returnShipment;
  draft.integratedShipmentOption = integratedShipmentOption;
  draft.customShipmentOption = customShipmentOption;

  if (integratedShipmentOption) {
    draft.ui.selectedShipmentOption = ReturnShippingOption.Integrated;
  } else if (customShipmentOption) {
    draft.ui.selectedShipmentOption = ReturnShippingOption.Custom;
  }
};

const clearReturnShipmentOptions = draft => {
  draft.ui.uiState = UiState.Idle;
  draft.ui.error = null;
  draft.returnShipmentOptions = undefined;
  draft.integratedShipmentOption = undefined;
  draft.currencyConversion = undefined;
  draft.customShipmentOption = undefined;
  draft.ui.selectedShipmentOption = undefined;
};

const setSelectedReturnShippingOption = (draft, action) => {
  draft.ui.selectedShipmentOption = action.payload.returnShippingOption;
};

const createCustomReturnShipmentRequest = draft => {
  draft.ui.uiState = UiState.Pending;
};

const createCustomReturnShipmentFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.uiState = UiState.Failure;
  draft.ui.error = error;
};

const createCustomReturnShipmentSuccess = draft => {
  draft.ui.uiState = UiState.Success;
  draft.ui.error = null;
};

const prepareReturnLabelOrderRequest = draft => {
  draft.ui.returnLabelOrder.prepareUiState = UiState.Pending;
};

const prepareReturnLabelOrderFailure = (draft, action) => {
  const {
    error
  } = action.payload;
  draft.ui.returnLabelOrder.prepareUiState = UiState.Failure;
  draft.ui.error = error;
};

const prepareReturnLabelOrderSuccess = (draft, action) => {
  const {
    returnShipmentOrder,
    currencyConversion
  } = action.payload;
  draft.ui.returnLabelOrder.prepareUiState = UiState.Success;
  draft.ui.error = null;
  draft.returnShipmentOrder = returnShipmentOrder;

  if (currencyConversion) {
    draft.currencyConversion = currencyConversion;
  }
};

const setReturnShipmentSelectedPaymentOptionSide = (draft, action) => {
  draft.ui.selectedPaymentOptionSide = action.payload.paymentOptionSide;
};

const setActiveModal = (draft, action) => {
  const {
    modal
  } = action.payload;
  draft.ui.activeModal = modal;
};

const cancelCheckout = draft => {
  draft.ui.uiState = UiState.Idle;
  draft.returnShipmentOrder = undefined;
};

const getReturnLabelOrderRequest = draft => {
  draft.ui.returnLabelOrder.fetchUiState = UiState.Pending;
  draft.returnShipmentOrder = undefined;
};

const getReturnLabelOrderFailure = draft => {
  draft.ui.returnLabelOrder.fetchUiState = UiState.Failure;
  draft.returnShipmentOrder = undefined;
};

const getReturnLabelOrderSuccess = (draft, action) => {
  draft.ui.returnLabelOrder.fetchUiState = UiState.Success;
  draft.returnShipmentOrder = action.payload.returnShipmentOrder;
};

const returnShipmentSlice = createSlice({
  name: stateName,
  initialState,
  reducers: {
    getReturnShipmentRequest,
    getReturnShipmentFailure,
    getReturnShipmentSuccess,
    getShipmentItemsRequest,
    getShipmentItemsFailure,
    getShipmentItemsSuccess,
    getReturnShippingOptionsRequest,
    getReturnShippingOptionsFailure,
    getReturnShippingOptionsSuccess,
    setSelectedReturnShippingOption,
    createCustomReturnShipmentRequest,
    createCustomReturnShipmentFailure,
    createCustomReturnShipmentSuccess,
    clearReturnShipmentOptions,
    prepareReturnLabelOrderRequest,
    prepareReturnLabelOrderFailure,
    prepareReturnLabelOrderSuccess,
    setReturnShipmentSelectedPaymentOptionSide,
    cancelCheckout,
    setActiveModal,
    getReturnLabelOrderRequest,
    getReturnLabelOrderFailure,
    getReturnLabelOrderSuccess
  }
});
export const {
  actions
} = returnShipmentSlice;
export const plug = {
  [stateName]: returnShipmentSlice.reducer
};
export default returnShipmentSlice.reducer;